import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, Loader2, Check, AlertTriangle, Plus, X, UserPlus } from 'lucide-react';
import './WeekGrid.css';
import { fold } from '../../utils/zoek';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, { ...options, headers: { ...(options.headers||{}), ...(token?{Authorization:`Bearer ${token}`}:{}) }, credentials: 'include' });
}

// ── Column definitions ──────────────────────────────────────────────────────
// Order/grouping is load-bearing: it must exactly match the API's HOUR_COLS.
const DAYS = [
  { key: 'ma', label: 'Maandag' },
  { key: 'di', label: 'Dinsdag' },
  { key: 'wo', label: 'Woensdag' },
  { key: 'do', label: 'Donderdag' },
  { key: 'vr', label: 'Vrijdag' },
];

const SUB_COLS = [
  { suffix: 'd', short: 'D', label: 'dag' },
  { suffix: 'o', short: 'O', label: 'overuren' },
  { suffix: 'an', short: 'A/N', label: 'avond/nacht' },
];

const SINGLE_COLS = [
  { key: 'za', label: 'Za', full: 'Zaterdag' },
  { key: 'zo', label: 'Zo', full: 'Zondag' },
];

// Feestdag hours are tracked PER DAY (rate = bedrag_7).
const FEESTDAG_COLS = [
  { key: 'ma_fd', label: 'Ma' }, { key: 'di_fd', label: 'Di' }, { key: 'wo_fd', label: 'Wo' },
  { key: 'do_fd', label: 'Do' }, { key: 'vr_fd', label: 'Vr' }, { key: 'za_fd', label: 'Za' },
  { key: 'zo_fd', label: 'Zo' },
];

// Flat, ordered list of the 24 hour field names — must exactly match the backend HOUR_COLS.
const FIELDS = [
  ...DAYS.flatMap((d) => SUB_COLS.map((s) => `${d.key}_${s.suffix}`)),
  ...SINGLE_COLS.map((s) => s.key),
  ...FEESTDAG_COLS.map((c) => c.key),
];

function emptyRow() {
  const row = {};
  for (const f of FIELDS) row[f] = 0;
  return row;
}

function rowTotal(row) {
  let sum = 0;
  for (const f of FIELDS) sum += Number(row[f]) || 0;
  return sum;
}

function formatNumber(n) {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

const SAVE_DEBOUNCE_MS = 700;
const SAVED_INDICATOR_MS = 1500;

// `apiBase` bepaalt tegen welke opslag het raster werkt: de urenlijst
// (/api/uren) of de Purchase Orders (/api/po). `extraParams` gaat mee in elke
// aanroep — de PO gebruikt dat voor order_id, zodat twee PO's in dezelfde week
// elk hun eigen regels houden.
export default function WeekGrid({ profiel, jaar, week, apiBase = '/api/uren', extraParams = null }) {
  const extraQuery = extraParams
    ? Object.entries(extraParams).map(([k, v]) => `&${k}=${encodeURIComponent(v)}`).join('')
    : '';
  // Kruisdraad: kolom waar je typt of met de muis staat geel markeren (rij gebeurt al via CSS).
  const [focusCol, setFocusCol] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);
  const activeCol = hoverCol || focusCol;
  const colOf = (e) => e.target.closest?.('[data-col]')?.getAttribute('data-col') || null;
  const [locked, setLocked] = useState(false); // week op slot -> alles alleen-lezen
  const [workers, setWorkers] = useState([]);          // full pool (for add + meta)
  const [addedIds, setAddedIds] = useState([]);        // worker_ids added to this week
  const [data, setData] = useState({});                // worker_id -> row (24 fields)
  const [saveStatus, setSaveStatus] = useState({});    // worker_id -> 'idle'|'saving'|'saved'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');            // filter added rows
  const [addQuery, setAddQuery] = useState('');        // add-person search
  const [addOpen, setAddOpen] = useState(false);

  const dataRef = useRef({});
  const timersRef = useRef({});
  const addBoxRef = useRef(null);

  const workersById = useMemo(() => {
    const m = {};
    for (const w of workers) m[w.id] = w;
    return m;
  }, [workers]);

  // ── Load pool + this week's added people ──────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    Object.values(timersRef.current).forEach(clearTimeout);
    timersRef.current = {};
    setLoading(true);
    setError(null);

    Promise.all([
      authFetch('/api/workers').then((r) => { if (!r.ok) throw new Error('workers'); return r.json(); }),
      authFetch(`${apiBase}/week?profiel=${encodeURIComponent(profiel)}&jaar=${encodeURIComponent(jaar)}&week=${encodeURIComponent(week)}${extraQuery}`)
        .then((r) => { if (!r.ok) throw new Error('week'); return r.json(); }),
    ])
      .then(([workersRes, weekRes]) => {
        if (cancelled) return;
        const workersArr = Array.isArray(workersRes) ? workersRes : [];
        const entries = Array.isArray(weekRes?.entries) ? weekRes.entries : [];
        setLocked(!!weekRes?.locked);
        const byId = {};
        for (const w of workersArr) byId[w.id] = w;

        const nextData = {};
        for (const e of entries) {
          const row = emptyRow();
          for (const f of FIELDS) row[f] = Number(e[f]) || 0;
          nextData[e.worker_id] = row;
        }
        // Added people = those with an entry, sorted by name.
        const ids = entries
          .map((e) => e.worker_id)
          .filter((id) => byId[id])
          .sort((a, b) => String(byId[a].name || '').localeCompare(String(byId[b].name || ''), 'nl', { sensitivity: 'base' }));

        setWorkers(workersArr);
        setAddedIds(ids);
        setData(nextData);
        dataRef.current = nextData;
        setSaveStatus({});
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError('Kon uren niet laden. Probeer het opnieuw.'); setLoading(false); } });

    return () => { cancelled = true; };
  }, [profiel, jaar, week, apiBase, extraQuery]);

  useEffect(() => () => { Object.values(timersRef.current).forEach(clearTimeout); }, []);

  // Close the add dropdown on outside click
  useEffect(() => {
    if (!addOpen) return;
    const onDoc = (e) => { if (addBoxRef.current && !addBoxRef.current.contains(e.target)) setAddOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [addOpen]);

  const putRow = useCallback((workerId, row) => {
    return authFetch(`${apiBase}/entry`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profiel, jaar, week, worker_id: workerId, ...(extraParams || {}), ...row }),
    });
  }, [profiel, jaar, week, apiBase, extraParams]);

  // Flush pending debounced saves immediately (fired right before a PDF export).
  useEffect(() => {
    const onFlush = () => {
      for (const id of Object.keys(timersRef.current)) {
        clearTimeout(timersRef.current[id]);
        delete timersRef.current[id];
        const row = dataRef.current[id];
        if (row) putRow(id, row).catch(() => {});
      }
    };
    window.addEventListener('uren:flush', onFlush);
    return () => window.removeEventListener('uren:flush', onFlush);
  }, [putRow]);

  const scheduleSave = useCallback((workerId) => {
    setSaveStatus((prev) => ({ ...prev, [workerId]: 'saving' }));
    if (timersRef.current[workerId]) clearTimeout(timersRef.current[workerId]);
    timersRef.current[workerId] = setTimeout(async () => {
      const row = dataRef.current[workerId];
      if (!row) return;
      try {
        const res = await putRow(workerId, row);
        if (!res.ok) throw new Error('save');
        setSaveStatus((prev) => ({ ...prev, [workerId]: 'saved' }));
        setTimeout(() => setSaveStatus((prev) => (prev[workerId] === 'saved' ? { ...prev, [workerId]: 'idle' } : prev)), SAVED_INDICATOR_MS);
      } catch {
        setSaveStatus((prev) => ({ ...prev, [workerId]: 'idle' }));
      } finally {
        delete timersRef.current[workerId];
      }
    }, SAVE_DEBOUNCE_MS);
  }, [putRow]);

  const handleCellChange = useCallback((workerId, field, rawValue) => {
    let num = Number(rawValue);
    if (rawValue === '' || Number.isNaN(num)) num = 0;
    if (num < 0) num = 0;
    setData((prev) => {
      const prevRow = prev[workerId] || emptyRow();
      const nextRow = { ...prevRow, [field]: num };
      const nextData = { ...prev, [workerId]: nextRow };
      dataRef.current = nextData;
      return nextData;
    });
    scheduleSave(workerId);
  }, [scheduleSave]);

  const addPerson = useCallback((workerId) => {
    if (locked) return; // week op slot: niets toevoegen
    if (addedIds.includes(workerId)) return;
    const row = emptyRow();
    const nextData = { ...dataRef.current, [workerId]: row };
    dataRef.current = nextData;
    setData(nextData);
    setAddedIds((prev) => [...prev, workerId]);
    setAddQuery('');
    setAddOpen(false);
    // Persist immediately so the person sticks to this week even with 0 hours.
    setSaveStatus((prev) => ({ ...prev, [workerId]: 'saving' }));
    putRow(workerId, row)
      .then((r) => { if (!r.ok) throw new Error(); setSaveStatus((prev) => ({ ...prev, [workerId]: 'saved' }));
        setTimeout(() => setSaveStatus((prev) => (prev[workerId] === 'saved' ? { ...prev, [workerId]: 'idle' } : prev)), SAVED_INDICATOR_MS); })
      .catch(() => setSaveStatus((prev) => ({ ...prev, [workerId]: 'idle' })));
  }, [addedIds, putRow, locked]);

  const removePerson = useCallback(async (workerId) => {
    if (locked) return; // week op slot: niemand verwijderen
    if (timersRef.current[workerId]) { clearTimeout(timersRef.current[workerId]); delete timersRef.current[workerId]; }
    const prevIds = addedIds;
    const prevData = { ...dataRef.current };
    setAddedIds((p) => p.filter((id) => id !== workerId));
    setData((p) => { const n = { ...p }; delete n[workerId]; dataRef.current = n; return n; });
    try {
      const res = await authFetch(
        `${apiBase}/entry?profiel=${encodeURIComponent(profiel)}&jaar=${jaar}&week=${week}&worker_id=${workerId}${extraQuery}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        // Server weigerde (bijv. week vergrendeld) — rij weer terugzetten in
        // plaats van net doen alsof het gelukt is.
        const d = await res.json().catch(() => ({}));
        setAddedIds(prevIds);
        setData(prevData);
        dataRef.current = prevData;
        window.alert(d.error || 'Verwijderen mislukt.');
      }
    } catch {
      setAddedIds(prevIds);
      setData(prevData);
      dataRef.current = prevData;
      window.alert('Verwijderen mislukt — probeer opnieuw.');
    }
  }, [profiel, jaar, week, locked, addedIds, apiBase, extraQuery]);

  const addable = useMemo(() => {
    const q = fold(addQuery).trim();
    const addedSet = new Set(addedIds);
    return workers
      .filter((w) => !addedSet.has(w.id) && (!q || fold(w.name).includes(q)))
      .slice(0, 8);
  }, [workers, addedIds, addQuery]);

  const visibleIds = useMemo(() => {
    const q = fold(search).trim();
    if (!q) return addedIds;
    return addedIds.filter((id) => fold(workersById[id]?.name).includes(q));
  }, [addedIds, search, workersById]);

  const withHours = useMemo(() => addedIds.filter((id) => rowTotal(data[id] || {}) > 0).length, [addedIds, data]);

  const COL_COUNT = 2 + DAYS.length * 3 + SINGLE_COLS.length + FEESTDAG_COLS.length; // +1 remove col below

  return (
    <div className="wg-wrap">
      {locked && (
        <div className="wg-locked-note">
          Deze week is vergrendeld. Uren, medewerkers toevoegen en verwijderen zijn geblokkeerd.
          Ontgrendel de week met de code om wijzigingen te maken.
        </div>
      )}
      <div className="wg-toolbar">
        <div className="wg-add" ref={addBoxRef}>
          <div className="wg-add-input">
            <UserPlus size={15} className="wg-search-icon" />
            <input
              type="text"
              placeholder={locked ? 'Week vergrendeld' : 'Medewerker toevoegen…'}
              value={addQuery}
              disabled={locked}
              onChange={(e) => { setAddQuery(e.target.value); setAddOpen(true); }}
              onFocus={() => setAddOpen(true)}
              aria-label="Medewerker toevoegen"
            />
          </div>
          {addOpen && (
            <div className="wg-add-menu">
              {addable.length === 0 && <div className="wg-add-empty">{addQuery ? 'Geen match' : 'Iedereen toegevoegd of typ om te zoeken'}</div>}
              {addable.map((w) => (
                <button key={w.id} className="wg-add-item" onMouseDown={(e) => { e.preventDefault(); addPerson(w.id); }}>
                  <Plus size={13} />
                  <span>{w.name}</span>
                  {!w.functiegroepId && <span className="wg-badge-notarief-sm">geen tarief</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {addedIds.length > 0 && (
          <div className="wg-search">
            <Search size={15} className="wg-search-icon" />
            <input type="text" placeholder="Filter…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Filter medewerkers" />
          </div>
        )}

        <div className="wg-summary">{addedIds.length} toegevoegd &middot; {withHours} met uren</div>
      </div>

      {loading && <div className="wg-state">Uren laden…</div>}
      {!loading && error && <div className="wg-state wg-state-error">{error}</div>}

      {!loading && !error && addedIds.length === 0 && (
        <div className="wg-state wg-empty-add">
          <UserPlus size={30} strokeWidth={1.3} />
          <p>Nog geen medewerkers in deze week. Voeg iemand toe met de zoekbalk hierboven.</p>
        </div>
      )}

      {!loading && !error && addedIds.length > 0 && (
        <div className="wg-scroll">
          {activeCol && (
            <style>{`.wg-table [data-col="${activeCol}"]{background:#fffbe0 !important}.wg-table [data-col="${activeCol}"] input{background:transparent}`}</style>
          )}
          <table className="wg-table"
            onFocus={(e) => setFocusCol(colOf(e))}
            onBlur={() => setFocusCol(null)}
            onMouseOver={(e) => setHoverCol(colOf(e))}
            onMouseLeave={() => setHoverCol(null)}>
            <thead>
              <tr>
                <th rowSpan={2} className="wg-th-corner">Naam</th>
                {DAYS.map((d) => (<th key={d.key} colSpan={3} className="wg-th-day">{d.label}</th>))}
                <th rowSpan={2} className="wg-th-single" data-col="za">Za</th>
                <th rowSpan={2} className="wg-th-single" data-col="zo">Zo</th>
                <th colSpan={FEESTDAG_COLS.length} className="wg-th-day wg-th-feest">Feestdag</th>
                <th rowSpan={2} className="wg-th-total">Totaal</th>
                <th rowSpan={2} className="wg-th-remove" aria-label="Verwijderen" />
              </tr>
              <tr>
                {DAYS.map((d) => SUB_COLS.map((s) => (
                  <th key={`${d.key}_${s.suffix}`} data-col={`${d.key}_${s.suffix}`} className="wg-th-sub" title={`${d.label} ${s.label}`}>{s.short}</th>
                )))}
                {FEESTDAG_COLS.map((c) => (<th key={c.key} data-col={c.key} className="wg-th-sub wg-th-feest" title={`Feestdag ${c.label}`}>{c.label}</th>))}
              </tr>
            </thead>
            <tbody>
              {visibleIds.length === 0 && (
                <tr><td colSpan={COL_COUNT + 1} className="wg-empty">Geen medewerkers gevonden.</td></tr>
              )}
              {visibleIds.map((id, idx) => {
                const w = workersById[id];
                if (!w) return null;
                const row = data[id] || emptyRow();
                const total = rowTotal(row);
                const status = saveStatus[id] || 'idle';
                const geenTarief = !w.functiegroepId;
                return (
                  <tr key={id} className={idx % 2 === 1 ? 'wg-zebra' : undefined}>
                    <td className="wg-td-name">
                      <div className="wg-name-cell">
                        <span className="wg-name-text">{w.name}</span>
                        {geenTarief && <span className="wg-badge-notarief" title="Geen functiegroep/tarief ingesteld"><AlertTriangle size={11} />geen tarief</span>}
                        <span className={`wg-save-indicator wg-save-${status}`}>
                          {status === 'saving' && <Loader2 size={12} className="wg-spin" />}
                          {status === 'saved' && <Check size={13} />}
                        </span>
                      </div>
                    </td>
                    {DAYS.map((d) => SUB_COLS.map((s) => {
                      const field = `${d.key}_${s.suffix}`;
                      const val = row[field];
                      return (
                        <td key={field} data-col={field} className="wg-td-cell">
                          <input type="number" inputMode="decimal" step="0.25" min="0"
                            value={val === 0 ? '' : val}
                            readOnly={locked} disabled={locked} onChange={(e) => handleCellChange(id, field, e.target.value)}
                            aria-label={`${d.label} ${s.label} uren voor ${w.name}`} />
                        </td>
                      );
                    }))}
                    {SINGLE_COLS.map((s) => {
                      const val = row[s.key];
                      return (
                        <td key={s.key} data-col={s.key} className="wg-td-cell">
                          <input type="number" inputMode="decimal" step="0.25" min="0"
                            value={val === 0 ? '' : val}
                            readOnly={locked} disabled={locked} onChange={(e) => handleCellChange(id, s.key, e.target.value)}
                            aria-label={`${s.full} uren voor ${w.name}`} />
                        </td>
                      );
                    })}
                    {FEESTDAG_COLS.map((c) => {
                      const val = row[c.key];
                      return (
                        <td key={c.key} data-col={c.key} className="wg-td-cell wg-td-feest">
                          <input type="number" inputMode="decimal" step="0.25" min="0"
                            value={val === 0 ? '' : val}
                            readOnly={locked} disabled={locked} onChange={(e) => handleCellChange(id, c.key, e.target.value)}
                            aria-label={`Feestdag ${c.label} uren voor ${w.name}`} />
                        </td>
                      );
                    })}
                    <td className="wg-td-total">{formatNumber(total)}</td>
                    <td className="wg-td-remove">
                      {!locked && (
                        <button className="wg-remove-btn" onClick={() => removePerson(id)} aria-label={`${w.name} verwijderen uit deze week`} title="Verwijderen uit deze week">
                          <X size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
